import * as React from "react";
import { connect } from "react-redux";
import { Row, Col, Label } from "reactstrap";
import { toastr } from "react-redux-toastr";
import ReactGA from "react-ga";
import { IAuthState } from "../../Auth/store/types";
import _ from "lodash";
import InfiniteScroll from "react-infinite-scroller";
import {
  initialStateMatchView,
  IRequestMatches,
  initialMatchResults,
  IMatchViewResponse,
  FilterOptions,
  MatchesProviders,
} from "../../../share/types/match";
import { IReduxState } from "../../../reduxTypes";
import MatchCard from "./components/MatchCard";
import SidebarContainer from "src/modules/Routing/Sidebar/SidebarContainer";
import { MatchesService } from "src/share/services/matches";
import "./matchesView.css";
import { MatchesCardLoader } from "src/share/components/ChartLoader/MatchesCardLoader";
import { FetchType } from "src/modules/Case/View/caseView.type";
import { MatchBiometricaCard } from "./components/MatchBiometricaCard";
import DateRange from "src/share/components/DateRange/DateRange";
import moment from "moment";
import { momentToMiliseconds } from "src/share/utils/utils";
import { SortBox } from "src/share/components/Filter/SortBox";
import { RoutesModules } from "src/modules/Routing/types";
import BiometricaCardLoader from "src/share/components/ChartLoader/BiometricaCardLoader";

const sortOptions = {
  date: "Date",
  similarity: "Similarity",
};

export interface IMatchesViewState {
  sort: any[];
  sortName: string;
  search: string;
  page: number;
  size: number;
  dateRange: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
    focus: "startDate" | "endDate" | null;
  };
  data: IMatchViewResponse;
  loading: boolean;
  fetchingData: boolean;
  pages: number;
  deleting: boolean;
  filterType: string;
  provider: MatchesProviders;
}

export interface IMatchesViewProps {
  auth: IAuthState;
  history: any;
  location: Location;
}

export class MatchesView extends React.PureComponent<
  IMatchesViewProps,
  IMatchesViewState
> {
  private clearRequest: IRequestMatches;
  constructor(props: IMatchesViewProps) {
    super(props);
    this.state = _.cloneDeep(initialStateMatchView);
    this.clearRequest = this.getSearchRequest();
  }

  public isBiometricaMatches = () =>
    this.props.location.pathname.includes("/biometrica");

  public async componentDidMount() {
    await this.fetchData(
      this.props.location.search ? FetchType.History : FetchType.Initial
    );
  }

  public async componentDidUpdate(prevProps: IMatchesViewProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.setState(_.cloneDeep(initialStateMatchView));
      await this.fetchData(
        this.props.location.search ? FetchType.History : FetchType.Initial
      );
    }
  }

  public getSearchRequest(
    type: FetchType = FetchType.Initial
  ): IRequestMatches {
    const isSort = type === FetchType.Sort;
    const size = this.state.data.matches.results.length;
    const filterType = this.state.filterType;
    const provider = this.getMatchesProvider();
    return {
      page: isSort ? 0 : size,
      size: 12,
      sort: this.state.sort,
      search: this.state.search,
      range: this.getRange(),
      filterType,
      provider,
    };
  }
  public getRange() {
    const range: any = {};
    if (
      this.state.dateRange &&
      this.state.dateRange.startDate &&
      this.state.dateRange.endDate &&
      !this.state.dateRange.focus
    ) {
      range.date = {
        gte: momentToMiliseconds(this.state.dateRange.startDate),
        lte: momentToMiliseconds(this.state.dateRange.endDate),
      };
    }
    return Object.keys(range).length > 0 ? range : null;
  }

  public goToTop() {
    const caseview = document
      .getElementsByClassName("matches-view")
      .item(0) as Element;
    caseview.scrollTop = 0;
  }

  public getMatchesProvider() {
    const isBiometrica = this.isBiometricaMatches();
    return isBiometrica
      ? MatchesProviders.Biometrica
      : MatchesProviders.Children;
  }

  public async fetchData(type: FetchType) {
    this.setState({ fetchingData: true });
    const isInfinite = type === FetchType.Infinite;
    const request = this.getSearchRequest();
    if (!isInfinite) {
      this.goToTop();
      this.setState({ data: _.cloneDeep(initialMatchResults), loading: true });
    }
    try {
      const respMatches = await MatchesService.getAllByAgency(request);
      const pages = Math.ceil(respMatches.matches.total / this.state.size);
      this.setState((state) => ({
        data: {
          matches: {
            results: [
              ...state.data.matches.results,
              ...respMatches.matches.results,
            ],
            total: respMatches.matches.total,
          },
        },
        fetchingData: false,
        loading: false,
        pages,
        provider: request.provider,
      }));
    } catch (error) {
      this.setState({ loading: false });
    }
  }

  public onSortChange(sort) {
    this.setState(
      {
        data: _.cloneDeep(initialMatchResults),
        sortName: sort,
        sort:
          sort === "date"
            ? [{ date: "desc" }]
            : sort === "similarity"
            ? [{ similarity: "desc" }]
            : [sort],
        fetchingData: true,
        loading: true,
      },
      () => this.fetchData(FetchType.Sort)
    );
  }

  public onSortFilterChange(sort) {
    this.setState(
      {
        data: _.cloneDeep(initialMatchResults),
        filterType: sort,
        fetchingData: true,
        loading: true,
      },
      () => this.fetchData(FetchType.Sort)
    );
  }

  public loadMore = (e) => {
    if (
      this.state.data.matches.results.length < this.state.data.matches.total &&
      e >= 1 &&
      !this.state.fetchingData
    ) {
      this.fetchData(FetchType.Infinite);
    }
  };

  public deleteMatch = (matchId: string) => async () => {
    try {
      this.setState({ deleting: true });
      await MatchesService.deleteMatchById(matchId);
      this.setState((state) => ({
        data: {
          ...state.data,
          matches: {
            ...state.data.matches,
            total: state.data.matches.total - 1,
            results: [
              ...state.data.matches.results.filter(
                (match) => match.matchId !== matchId
              ),
            ],
          },
        },
        deleting: false,
      }));
      ReactGA.event({
        category: "Match",
        action: "Discard match",
        label: "Matches management",
      });
    } catch (error) {
      this.setState({ loading: false, deleting: false });
      toastr.error("There was a problem", "The match could not be discard");
    }
  };
  public onDatesChange = (date) => {
    this.setState(
      (state) => ({ ...state, dateRange: { ...state.dateRange, ...date } }),
      () => {
        if (
          !this.state.dateRange.focus &&
          this.state.dateRange.startDate &&
          this.state.dateRange.endDate &&
          (date.startDate || date.endDate)
        ) {
          this.fetchData(FetchType.Filter);
        }
      }
    );
  };
  private isFiltering() {
    return (
      JSON.stringify(_.omit(this.clearRequest, ["page"])) !==
      JSON.stringify(_.omit(this.getSearchRequest(), ["page"]))
    );
  }
  public clearSearch = async () => {
    await new Promise((res) =>
      this.setState({ ..._.cloneDeep(initialStateMatchView) }, res as () => void)
    );
    this.fetchData(FetchType.Initial);
  };

  public render() {
    const provider = this.getMatchesProvider();
    return (
      <>
        <div className="filters-box matches">
          <div className="results-count">
            Results: {this.state.data.matches.total}{" "}
            {this.state.data.matches.total === 1 ? "match" : "matches"}
          </div>
          <Label className={"dateLabel"}>Date filter</Label>
          <DateRange
            onDatesChange={this.onDatesChange}
            onFocusChange={this.onDatesChange}
            focusedInput={this.state.dateRange.focus}
            startDate={this.state.dateRange.startDate}
            endDate={this.state.dateRange.endDate}
          />
          <SortBox
            onSort={(key) => this.onSortFilterChange(key)}
            options={FilterOptions}
            selected={this.state.sortName}
            disabled={this.state.fetchingData}
            label={"Filter by"}
          />
          <SortBox
            onSort={(key) => this.onSortChange(key)}
            options={sortOptions}
            selected={this.state.sortName}
            disabled={this.state.fetchingData}
          />
          {this.isFiltering() && (
            <div className="clr-search">
              <b onClick={this.clearSearch}>Clear search</b>
            </div>
          )}
        </div>
        <div className="matches-view">
          <InfiniteScroll
            pageStart={0}
            loadMore={this.loadMore}
            hasMore={this.state.page <= this.state.pages}
            useWindow={false}
          >
            <Row style={{ margin: "0" }}>
              {!this.state.loading &&
              this.state.data &&
              this.state.data.matches.results.length > 0
                ? this.state.data.matches.results.map((match) =>
                    this.state.provider === MatchesProviders.Biometrica ? (
                      <MatchBiometricaCard
                        auth={this.props.auth}
                        key={match.matchId}
                        matchData={match}
                        history={this.props.history}
                        deleteMatch={this.deleteMatch}
                        deleting={this.state.deleting}
                      />
                    ) : (
                      <MatchCard
                        key={match.matchId}
                        matchData={match}
                        history={this.props.history}
                        deleteMatch={this.deleteMatch}
                        deleting={this.state.deleting}
                      />
                    )
                  )
                : null}
              {!this.state.loading &&
              !this.state.fetchingData &&
              this.state.data.matches.results.length === 0 ? (
                <h4 className="no-results">No data found</h4>
              ) : null}
            </Row>
            {this.state.loading ? this.loader(12) : null}
            {this.state.fetchingData && !this.state.loading
              ? this.loader(12)
              : null}
          </InfiniteScroll>
          {this.state.loading && this.state.fetchingData && (
            <Row style={{ margin: "0" }}>
              {Array.apply(null, { length: 12 }).map((data, index) => (
                <Col
                  key={index}
                  xl="4"
                  lg="6"
                  md="12"
                  sm="12"
                  xs="12"
                  className="card-column"
                >
                  {provider === MatchesProviders.Biometrica ? (
                    <BiometricaCardLoader />
                  ) : (
                    <MatchesCardLoader />
                  )}
                </Col>
              ))}
            </Row>
          )}
        </div>
      </>
    );
  }
  public loader(length) {
    const provider = this.getMatchesProvider();
    return (
      <Row>
        {Array.apply(null, { length }).map((data, index) =>
          provider === MatchesProviders.Biometrica ? (
            <BiometricaCardLoader />
          ) : (
            <Col
              key={index}
              xl="4"
              lg="6"
              md="12"
              sm="12"
              xs="12"
              className="card-column"
            >
              <MatchesCardLoader />
            </Col>
          )
        )}
      </Row>
    );
  }
}

const mapStateToProps = (state: IReduxState) => ({
  auth: state.auth,
});

export default SidebarContainer(RoutesModules.SEARCH)(
  connect(mapStateToProps)(MatchesView)
);
